import { generateUtcTimeTicks } from './utcTimeTicks';
import { toNumberTimestamp } from '../../../util.js';

type Unit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
// Local calendar boundaries match local display, including DST and month length.
export const calendarTicks = (domain: (string | number | Date)[], interval: readonly [Unit, number], limit = Infinity): Date[] => {
  const values = domain.map(toNumberTimestamp).filter((v): v is number => v !== undefined);
  if (values.length < 2) return [];
  const min = Math.min(...values), max = Math.max(...values);
  const [unit, step] = interval;
  if (!Number.isInteger(step) || step < 1) throw new RangeError("Tick step must be a positive integer.");
  if (!(limit >= 0) || (Number.isFinite(limit) && !Number.isInteger(limit))) throw new RangeError("Invalid tick limit.");
  const date = new Date(min);
  date.setMilliseconds(0);
  if (unit === 'second') date.setSeconds(Math.floor(date.getSeconds() / step) * step);
  else {
    date.setSeconds(0);
    if (unit === 'minute') date.setMinutes(Math.floor(date.getMinutes() / step) * step);
    else {
      date.setMinutes(0);
      if (unit === 'hour') date.setHours(Math.floor(date.getHours() / step) * step);
      else {
        date.setHours(0);
        if (unit === 'day') date.setDate(1 + Math.floor((date.getDate() - 1) / step) * step);
        if (unit === 'week') date.setDate(date.getDate() - (date.getDay() + 6) % 7);
        if (unit === 'month') { date.setDate(1); date.setMonth(Math.floor(date.getMonth() / step) * step); }
        if (unit === 'year') { date.setMonth(0, 1); date.setFullYear(Math.floor(date.getFullYear() / step) * step); }
      }
    }
  }
  const advance = () => {
    if (unit === 'second') date.setTime(+date + step * 1000);
    if (unit === 'minute') date.setTime(+date + step * 60000);
    if (unit === 'hour') date.setHours(date.getHours() + step);
    if (unit === 'day') date.setDate(date.getDate() + step);
    if (unit === 'week') date.setDate(date.getDate() + 7 * step);
    if (unit === 'month') date.setMonth(date.getMonth() + step);
    if (unit === 'year') date.setFullYear(date.getFullYear() + step);
  };
  if (+date < min) advance();
  const ticks: Date[] = [];
  while (+date <= max) {
    if (ticks.length >= limit) throw new RangeError("Time tick limit exceeded; no partial tick list returned.");
    ticks.push(new Date(date));
    const previous = +date;
    advance();
    if (!(+date > previous)) throw new RangeError("Time tick interval could not advance.");
  }
  return ticks;
};

const timeSpan = (domain: (string | number | Date)[]): number => {
  const values = domain.map(toNumberTimestamp).filter((v): v is number => v !== undefined);
  return values.length > 1 ? Math.max(...values) - Math.min(...values) : 0;
};

export const formatTimeTick = (value: Date, domain: (string | number | Date)[] = []): string => {
  const span = timeSpan(domain);
  if (span < 86400000) return value.toLocaleTimeString(undefined, {hour:'2-digit',minute:'2-digit',hour12:false});
  const date = `${String(value.getMonth() + 1).padStart(2, '0')}/${String(value.getDate()).padStart(2, '0')}/${value.getFullYear()}`;
  return span < 7 * 86400000 ? `${value.toLocaleDateString(undefined, {weekday:'short'})} ${date}` : date;
};

// Count is a target; choose round UTC boundaries rather than numeric timestamps.
export const generateTimeTicks = (domain: (string | number | Date)[], count = 5): Date[] =>
  generateUtcTimeTicks(domain, count);
